<template>
  <div v-if="isOpen" class="switch-practice-dialog">
    <div class="dialog-overlay" @click="handleOverlayClick">
      <div class="dialog-container" @click.stop>
        <!-- Header -->
        <div class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.99 11L3 15L6.99 19V16H14V14H6.99V11ZM21 9L17.01 5V8H10V10H17.01V13L21 9Z" fill="white" />
              </svg>
            </div>
            <h2 class="dialog-title">Switch To Associated Practice</h2>
          </div>
          <button class="dialog-close-btn" @click="closeDialog">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.5833 8.07329L19.9267 6.41663L14 12.3433L8.07333 6.41663L6.41667 8.07329L12.3433 14L6.41667 19.9266L8.07333 21.5833L14 15.6566L19.9267 21.5833L21.5833 19.9266L15.6567 14L21.5833 8.07329Z" fill="white" />
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="dialog-content">
          <p class="dialog-message">Please choose a practice: </p>

          <!-- Clinic Selector -->
          <div v-if="!selectedClinic" class="clinic-selector">
            <SingleSelector
              :options="clinicOptions"
              :model-value="selectedClinicId"
              :loading="isLoading"
              placeholder="Choose Practice"
              @update:model-value="handleClinicSelect"
            />
          </div>

          <!-- Selected Clinic Details -->
          <div v-if="selectedClinic" class="practice-card">
            <div class="practice-header">
              <div class="practice-logo">
                <img v-if="practiceLogo" :src="practiceLogo" alt="Practice logo" />
                <div v-else class="practice-logo-placeholder">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 0C8.95 0 0 8.95 0 20C0 31.05 8.95 40 20 40C31.05 40 40 31.05 40 20C40 8.95 31.05 0 20 0ZM20 6C23.32 6 26 8.68 26 12C26 15.32 23.32 18 20 18C16.68 18 14 15.32 14 12C14 8.68 16.68 6 20 6ZM20 34.4C15 34.4 10.58 31.84 8 27.96C8.06 23.98 16 21.8 20 21.8C23.98 21.8 31.94 23.98 32 27.96C29.42 31.84 25 34.4 20 34.4Z" fill="#C4C4C4" />
                  </svg>
                </div>
              </div>
              <div class="practice-info">
                <p class="practice-user">{{ userName }}</p>
                <h3 class="practice-name">{{ practiceName }}</h3>
              </div>
            </div>

            <div class="practice-details">
              <p class="practice-detail-item">
                <span class="detail-label">Practice ID: </span>
                <span class="detail-value">{{ practiceId }}</span>
              </p>
              <p class="practice-detail-item">
                <span class="detail-label">Practice Address: </span>
                <span class="detail-value">{{ practiceAddress }}</span>
              </p>
              <p class="practice-detail-item">
                <span class="detail-label">Role: </span>
                <span class="detail-value">{{ userRole }}</span>
              </p>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="dialog-footer">
          <Button
            v-if="selectedClinic"
            type="normal"
            colorType="none-stroke"
            class="btn-back"
            @click="goBack"
          >
            ← Back to Selection
          </Button>
          <Button
            v-if="!selectedClinic"
            type="normal"
            colorType="none-stroke"
            class="btn-cancel"
            @click="closeDialog"
          >
            Cancel
          </Button>
          <Button
            v-if="selectedClinic"
            type="normal"
            colorType="dark-blue"
            class="btn-confirm"
            :disabled="isSwitching"
            :loading="isSwitching"
            @click="confirmSwitch"
          >
            {{ isSwitching ? 'Switching...' : 'Yes, switch.' }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import './AssociatedPracticeDialog.css';
import { getCustomerClinicsApi, switchClinicForCustomerApi } from '@/api/login';
import sessionManager, { ensureFreshToken } from '@/utils/sessionManager';
import { notify } from '@/utils/notify';
import SingleSelector from '@/NewLibrary/SingleSelector.vue';
import Button from '@/NewLibrary/Button.vue';

export default {
  name: 'AssociatedPracticeDialog',
  components: {
    SingleSelector,
    Button,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const store = useStore();
    const loginUserInfo = computed(() => store.state.login.userInfo);
    
    return {
      store,
      loginUserInfo,
    };
  },
  data() {
    return {
      availableClinics: [],
      selectedClinicId: '',
      selectedClinic: null,
      isLoading: false,
      isSwitching: false,
    };
  },
  computed: {
    userName() {
      const info = this.loginUserInfo;
      if (info?.customer_first_name || info?.customer_last_name) {
        return [info.customer_first_name, info.customer_last_name].filter(Boolean).join(' ');
      }
      return info?.customer_name || '';
    },
    practiceName() {
      return this.selectedClinic?.clinic_name || '';
    },
    practiceId() {
      return this.selectedClinic?.clinic_id?.toString() || '';
    },
    practiceAddress() {
      return 'Practice address information not available';
    },
    userRole() {
      const isAdmin = this.store.getters['myPractice/userInfo/isAdmin'];
      return isAdmin ? 'Admin' : 'Provider';
    },
    practiceLogo() {
      return null;
    },
    clinicOptions() {
      return (this.availableClinics || []).map((clinic) => ({
        label: `${clinic.clinic_name} (ID: ${clinic.clinic_id})`,
        value: String(clinic.clinic_id),
      }));
    },
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.fetchClinicList();
      } else {
        this.resetDialog();
      }
    },
  },
  methods: {
    async fetchClinicList() {
      if (!this.loginUserInfo?.customer_id) {
        notify({
          title: 'Error',
          message: 'Customer ID not found',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      this.isLoading = true;
      try {
        const response = await getCustomerClinicsApi({
          customer_ids: [this.loginUserInfo.customer_id.toString()],
        });

        const customerClinics = response.data?.customer_clinics?.[0];
        if (customerClinics?.clinics) {
          const currentClinicId = this.loginUserInfo.clinic_id;
          this.availableClinics = customerClinics.clinics.filter(
            clinic => clinic.clinic_id !== currentClinicId && clinic.isActive
          );

          if (this.availableClinics.length === 0) {
            notify({
              title: 'Info',
              message: 'No other active practices available',
              type: 'info',
              duration: 3000,
            });
          }
        }
      } catch (error) {
        console.error('Error fetching clinic list:', error);
        notify({
          title: 'Error',
          message: 'Failed to fetch practice list',
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },
    handleClinicSelect(value) {
      this.selectedClinicId = value;
      const clinic = this.availableClinics.find(
        c => c.clinic_id === parseInt(value)
      );
      this.selectedClinic = clinic;
    },
    goBack() {
      this.selectedClinic = null;
      this.selectedClinicId = '';
    },
    resetDialog() {
      this.availableClinics = [];
      this.selectedClinicId = '';
      this.selectedClinic = null;
      this.isLoading = false;
      this.isSwitching = false;
    },
    closeDialog() {
      this.$emit('close');
    },
    handleOverlayClick() {
      this.closeDialog();
    },
    async confirmSwitch() {
      if (!this.selectedClinic) return;

      this.isSwitching = true;
      try {
        // Rotate the access token now if it's within the refresh window. The
        // patientListService interceptor would otherwise run ensureFreshToken()
        // AFTER this snapshot, sending a freshly-minted Bearer header while the
        // query param below still carried the pre-refresh token — the two would
        // diverge on the same transferCustomerClinic request.
        await ensureFreshToken();
        const token = sessionStorage.getItem('token');
        const response = await switchClinicForCustomerApi({
          token,
          clinic_id: this.selectedClinic.clinic_id.toString(),
        });

        if (response.data?.token) {
          const newToken = response.data.token;

          // Save the new token to Vuex store (which will persist to sessionStorage).
          // Explicitly clear the refresh token: the existing one was issued under
          // the PREVIOUS clinic, and SET_TOKEN would otherwise keep it (omitted
          // refresh fields are preserved). Carrying it over lets sessionManager
          // later refresh into a previous-clinic access token and silently switch
          // the user back. It is re-established for the new clinic just below.
          this.store.dispatch('login/setToken', {
            token: newToken,
            userId: '',
            exp: 0,
            refreshToken: '',
            refreshTokenExpiry: 0,
            authToken: '',
            rc_user_id: '',
            ws_url: '',
            userInfo: null
          });

          // Treat the switch like a new session: exchange the new JWT for a
          // refresh token bound to the NEW clinic and mint a fresh access token
          // (with its own expiry) from it. If this fails (e.g. backend endpoints
          // unavailable) we fall back to running on the switch JWT alone — safe,
          // because the stale refresh token was already cleared above.
          const ok = await sessionManager.bootstrapNewSession(newToken);
          if (!ok) {
            console.warn('[ClinicSwitch] Refresh-token bootstrap failed; using the switch JWT directly.');
          }

          notify({
            title: 'Success',
            message: `Switched to ${this.selectedClinic.clinic_name}`,
            type: 'success',
            duration: 2000,
          });

          this.closeDialog();
          
          // Reload page to reinitialize everything with new token
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      } catch (error) {
        console.error('Error switching clinic:', error);
        notify({
          title: 'Error',
          message: 'Failed to switch practice',
          type: 'error',
          duration: 3000,
        });
      } finally {
        this.isSwitching = false;
      }
    },
  },
};
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.associated-practice-dialog {
  background: #ffffff;
  border-radius: 10px;
  width: 640px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.15);
}
</style>
